// Corbel CRM — customer-facing release notes.
// SINGLE SOURCE OF TRUTH for the app version shown across the app.
// To ship an update: add a new entry at the TOP of `releaseNotes` and bump its
// `version`. `APP_VERSION` automatically reflects the newest entry, so the
// version badge in Settings + the "What's New" page update together each release.

export const releaseNotes = [
  {
    version: '2.6.0',
    date: '2026-06-08',
    title: 'Estimate-to-invoice & smarter billing',
    summary: 'Turn estimates into invoices, bill deposits and progress draws, and track every contract.',
    changes: [
      { type: 'new', text: 'Convert an estimate into an invoice in one click — from the project/lead estimates list, or right on the New Invoice page after picking a client.' },
      { type: 'new', text: 'Combine multiple estimates onto a single invoice — perfect when a client decides to do two separately-bid jobs at once.' },
      { type: 'new', text: 'Bill a percentage of the total — 10 / 25 / 33.3 / 50 / 100% or custom — with a new "Initial Invoice (Deposit)" type that defaults to a 50% deposit.' },
      { type: 'new', text: 'Billing Progress on every project: see invoiced vs. remaining, the % of the contract billed, and how much you\'ve collected — at a glance.' },
      { type: 'improved', text: 'Client invoices show the scope of work and totals only — your per-line pricing stays private. Owners can still print a full internal copy.' },
      { type: 'improved', text: 'Per-line "Tax" checkbox: tax applies only to lines you choose. Estimates pulled into an invoice are tax-inclusive, so they\'re never taxed twice.' },
      { type: 'fixed', text: 'Fixed a crash when selecting a client on the New Invoice page.' },
    ],
  },
  {
    version: '2.5.0',
    date: '2026-06-03',
    title: 'Personal Scheduling Calendar',
    summary: 'A clean, native calendar built for your day in the field.',
    changes: [
      { type: 'new', text: 'New Day / Week / Month calendar with drag-and-drop and resize rescheduling.' },
      { type: 'new', text: 'Color-coded event types — Fieldwork, Site Visit, Client Meeting, Internal — with Tentative/Confirmed status and quick show/hide filters.' },
      { type: 'new', text: 'Create, edit, and link events to a lead or project right from the calendar.' },
      { type: 'improved', text: "Optional per-event push to your own Google Calendar — never to teammates' calendars." },
      { type: 'fixed', text: 'Converting a lead to a project now carries over the lead\'s estimates and uploaded files (and existing converted projects were backfilled).' },
    ],
  },
  {
    version: '2.4.0',
    date: '2026-06-03',
    title: 'Team assignments & owner defaults',
    summary: 'Assign leads and projects to your team — with the owner covered by default.',
    changes: [
      { type: 'new', text: 'Assign or reassign any lead or project to yourself (Owner) or a Manager.' },
      { type: 'new', text: 'New projects include an "Assigned Manager" dropdown right on the create form.' },
      { type: 'new', text: 'Filter Leads and Projects to "Mine" with one click, and see each item\'s assignee at a glance.' },
      { type: 'improved', text: 'The Owner now appears on the Team page and is the default assignee for every lead and project until you delegate it to a manager.' },
    ],
  },
  {
    version: '2.3.0',
    date: '2026-06-02',
    title: 'Estimate PDFs & a smarter client portal',
    summary: 'Send professional estimates and give clients on quick jobs a cleaner update.',
    changes: [
      { type: 'new', text: 'Export any estimate as a polished, branded PDF you can email or print for clients.' },
      { type: 'new', text: 'The client portal now adapts to small jobs — clients see a simple task checklist instead of full construction phases.' },
      { type: 'improved', text: 'Faster, more reliable charts and behind-the-scenes security hardening.' },
    ],
  },
  {
    version: '2.2.0',
    date: '2026-05-21',
    title: 'Job costing & flexible billing',
    summary: 'Know your numbers on every job and bill the way you work.',
    changes: [
      { type: 'new', text: 'Track expenses by category per job and see Budget vs. Actual with estimated profit at a glance.' },
      { type: 'new', text: 'Attach receipt photos to expenses for clean record-keeping.' },
      { type: 'new', text: 'Progress billing, partial payments, and one-click "Mark as Paid".' },
      { type: 'new', text: 'Export all invoices to CSV for your accountant.' },
    ],
  },
  {
    version: '2.1.0',
    date: '2026-05-06',
    title: 'Estimating tools',
    summary: 'Build and win more bids, faster.',
    changes: [
      { type: 'new', text: 'Import estimates directly from Craftsman National Estimator Excel files.' },
      { type: 'new', text: 'Labor costs auto-calculate from craft codes — edit any line and totals update instantly.' },
      { type: 'new', text: 'Turn a winning estimate into a project (and move the lead to Won) in one click.' },
    ],
  },
  {
    version: '2.0.0',
    date: '2026-04-16',
    title: 'Reporting, jobsite photos & mobile',
    summary: 'See your business clearly and capture work from the field.',
    changes: [
      { type: 'new', text: 'Advanced Reporting dashboard with 10 interactive charts for pipeline, revenue, leads, and project health.' },
      { type: 'new', text: 'Upload jobsite photos — including iPhone HEIC photos — with automatic conversion and compression.' },
      { type: 'improved', text: 'A faster, cleaner experience optimized for phones and tablets in the field.' },
      { type: 'improved', text: 'Tightened file storage security across the platform.' },
    ],
  },
];

// The current app version is always the newest release entry.
export const APP_VERSION = releaseNotes[0].version;
export const LATEST_RELEASE = releaseNotes[0];
